import moment from "moment";
import "moment-timezone";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import errorWhiteSvg from "assets/img/error-white.svg";
import { Body } from "components/Typography";
import { State } from "state";
import { unsetError, FileUploadState } from "state/create_file_upload";
import { fetchStoresByProjectId } from "state/stores";
import { PaginatedQuery } from "interfaces/paginated-query";
import { LightThemeTable } from "components/LightThemeTable";
import { Link } from "react-router-dom";


interface MappingPreviewOwnProps {
  prefix: string;
  errorPrefixes?: string[];
  showError?: boolean;
  projectId: string;
}

type MappingPreviewProps = MappingPreviewOwnProps & PropsFromRedux;

class ProjectPreview extends React.Component<MappingPreviewProps> {
  private MAPPING_PREVIEW_PAGE_SIZE = 10;
  private MAPPING_PREVIEW_DEFAULT_SORT = "";

  private defaultPaginationParams: PaginatedQuery = {
    sort: this.MAPPING_PREVIEW_DEFAULT_SORT,
    size: this.MAPPING_PREVIEW_PAGE_SIZE,
    page: 0,
  };

  public state = { 
    ...this.defaultPaginationParams, 
    initialMountComplete: false,
    currentPage: 1,
    limit: 10,
  };

  public componentDidMount() {
    const { projectId } = this.props;
    this.props.fetchStoresByProjectId(projectId);
    this.setState({ initialMountComplete: true });
  }

  public componentWillUnmount() {
    const { prefix, errorPrefixes } = this.props;

    this.props.unsetError(prefix);
    if (errorPrefixes) {
      errorPrefixes.forEach((errorPrefix) => this.props.unsetError(errorPrefix));
    }
  }

  public componentDidUpdate(prevProps: MappingPreviewProps) {
    const { projectId } = this.props;

    // Refresh when upload completes
    if (
      (prevProps.upload.loading && !this.props.upload.loading && !this.props.upload.hasError) ||
      (prevProps.errorUpload &&
        prevProps.errorUpload.loading &&
        this.props.errorUpload &&
        !this.props.errorUpload.loading &&
        !this.props.errorUpload.hasError)
    ) {
      this.props.fetchStoresByProjectId(projectId);
    }
  }

  private handlePageChange = (page: number, newLimit: number) => {
    this.setState({ currentPage: page, limit: newLimit });
  };

  public render() {
    const {
      upload: { hasError: defaultError, errors: defaultErrors = [] },
      errorUpload,
      stores,
      showError = true,
    } = this.props;

    if (!stores) return null;

    const hasError = defaultError || (errorUpload && errorUpload.hasError);
    const errors: string[] = defaultErrors.concat((errorUpload && errorUpload.errors) || []);

    const cellStyle: React.CSSProperties = { paddingTop: "8px", paddingBottom: "8px" };

    const tableColumnsConfig = [
      {
        field: "store_number",
        header: "Store No",
        formatter: (_: any, row: any) => (
            <div style={cellStyle}>
              {row?.store_number ? (
                <Link
                  to={`/projects/${this.props.projectId}/stores/${row.store_id}`}
                  style={{ color: "#E5B611", textDecoration: "underline", cursor: "pointer" }}
                >
                  {row.store_number}
                </Link>
              ) : (
                "-"
              )}
            </div>
          ),
      },
      {
        field: "state",
        header: "State",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>{row?.state || "-"}</div>
        ),
      },
      {
        field: "comment_cnt",
        header: "File Count",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>{row?.comment_cnt || "-"}</div>
        ),
      },
      {
        field: "store_address",
        header: "Address",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>{row?.store_address || "-"}</div>
        ),
      },

      {
        field: "store_validation_deadline",
        header: "Validation Deadline",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>
            {row.store_validation_deadline
              ? moment(row.store_validation_deadline)
                // .tz("America/Chicago")
                .format("MM/DD/YYYY")
              : ""}
          </div>
        ),
      }
    ];

    return (
      <>
        {hasError && (
          <div className="error card-error">
            <img src={errorWhiteSvg} alt="Error Icon" />
            <p className="text">Error: File formatting. See above for details.</p>
          </div>
        )}
 
        {this.state.initialMountComplete && (
          <LightThemeTable
            data={stores}
            columns={tableColumnsConfig}
            onPageChange={this.handlePageChange}
            pageSizeOptions={[5, 10, 20, 100, 200]}
            isLoading={this.props.loadingStores}
          />
        )}

        {showError && hasError && (
          <div className="card-error-bottom">
            <h5 className="error">Errors:</h5>
            <ul className="error-list">
              {errors.map((e) => (
                <li key={e}>
                  <Body type="3">{e}</Body>
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  }
}

const connecter = connect(
  (state: State, ownProps: MappingPreviewOwnProps) => {
    const { prefix, errorPrefixes = [] } = ownProps;

    let errorUpload: FileUploadState = { hasError: false };
    errorPrefixes.some((errorPrefix) => {
      // @ts-ignore
      if (state[`${errorPrefix}_file_upload`].hasError) {
        // @ts-ignore
        errorUpload = state[`${errorPrefix}_file_upload`];
        return true;
      }
      return false;
    });

    return {
      errorUpload,
      upload: {
        // @ts-ignore
        ...state[`${prefix}_file_upload`],
        hasError: errorUpload.hasError,
      },
      stores: state.stores.content?.data,
      loadingStores: state.stores.loading,
    };
  },
  { fetchStoresByProjectId, unsetError }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default connecter(ProjectPreview);
